import React, { useRef } from "react";
import { useForm } from "@tanstack/react-form";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RichEditor } from "@/components/ui/rich-editor";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2, Plus, X, Upload, ImagePlus, ChevronDown, Check, GripVertical } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { useActiveStore } from "@/hooks/use-active-store";
import {
  useCreateProduct,
  useUpdateProduct,
  useProductCategories,
  useUploadProductImage,
  useDeleteProductImage,
  useBatchVariations,
} from "@/hooks/use-products";

type Props = {
  product?: any;
  onSuccess: () => void;
};

type Attribute = { name: string; options: string };
type VariationRow = {
  id?: number;
  combination: Record<string, string>;
  regular_price: string;
  sale_price: string;
  sku: string;
  stock_quantity: string;
  stock_status: string;
  image?: { id: number; src: string } | null;
};

type FormValues = {
  name: string;
  status: "publish" | "draft";
  type: string;
  description: string;
  short_description: string;
  regular_price: string;
  sale_price: string;
  tax_status: string;
  tax_class: string;
  sku: string;
  manage_stock: boolean;
  stock_quantity: string;
  stock_status: string;
  categories: number[];
  attributes: Attribute[];
  variants: VariationRow[];
  featuredImage: any;
  galleryImages: any[];
};

function parseOptions(str: string): string[] {
  return str.split(",").map((s) => s.trim()).filter(Boolean);
}

function cartesian(attrs: Attribute[]): Record<string, string>[] {
  const validAttrs = attrs.filter((a) => a.name.trim() && parseOptions(a.options).length > 0);
  if (validAttrs.length === 0) return [];
  return validAttrs.reduce<Record<string, string>[]>(
    (acc, attr) => {
      const opts = parseOptions(attr.options);
      return acc.flatMap((combo) => opts.map((opt) => ({ ...combo, [attr.name.trim()]: opt })));
    },
    [{}]
  );
}

function comboKey(combo: Record<string, string>): string {
  return Object.entries(combo).map(([k, v]) => `${k}:${v}`).sort().join("|");
}

function buildDefaultValues(product?: any): FormValues {
  if (!product) {
    return {
      name: "",
      status: "draft",
      type: "simple",
      description: "",
      short_description: "",
      regular_price: "",
      sale_price: "",
      tax_status: "taxable",
      tax_class: "",
      sku: "",
      manage_stock: false,
      stock_quantity: "",
      stock_status: "instock",
      categories: [],
      attributes: [],
      variants: [],
      featuredImage: null,
      galleryImages: [],
    };
  }

  const images = product.images || [];
  return {
    name: product.name || "",
    status: product.status === "draft" ? "draft" : "publish",
    type: product.type || "simple",
    description: product.description || "",
    short_description: product.short_description || "",
    regular_price: product.regular_price || "",
    sale_price: product.sale_price || "",
    tax_status: product.tax_status || "taxable",
    tax_class: product.tax_class || "",
    sku: product.sku || "",
    manage_stock: product.manage_stock || false,
    stock_quantity: product.stock_quantity != null ? String(product.stock_quantity) : "",
    stock_status: product.stock_status || "instock",
    categories: product.categories?.map((c: any) => c.id) || [],
    attributes: product.attributes?.map((a: any) => ({
      name: a.name,
      options: (a.options || []).join(", "),
    })) || [],
    variants: product.variations?.map((v: any) => ({
      id: v.id,
      combination: Object.fromEntries(
        (v.attributes || []).map((a: any) => [a.name, a.option])
      ),
      regular_price: v.regular_price || "",
      sale_price: v.sale_price || "",
      sku: v.sku || "",
      stock_quantity: v.stock_quantity != null ? String(v.stock_quantity) : "",
      stock_status: v.stock_status || "instock",
      image: v.image ? { id: v.image.id, src: v.image.src } : null,
    })) || [],
    featuredImage: images[0] || null,
    galleryImages: images.slice(1),
  };
}

export function ProductForm({ product, onSuccess }: Props) {
  const isEdit = !!product;
  const { storeId } = useActiveStore();
  const createMut = useCreateProduct(storeId);
  const updateMut = useUpdateProduct(storeId);
  const uploadMut = useUploadProductImage(storeId);
  const deleteImageMut = useDeleteProductImage(storeId);
  const batchVarMut = useBatchVariations(storeId);

  const { data: categoryList } = useProductCategories(storeId);

  const [defaultPrice, setDefaultPrice] = React.useState("");
  const [defaultStock, setDefaultStock] = React.useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const galleryInputRef = useRef<HTMLInputElement>(null);

  const form = useForm({
    defaultValues: buildDefaultValues(product),
    onSubmit: async ({ value }) => {
      if (!value.name.trim()) {
        toast.error("Naziv proizvoda je obavezan");
        return;
      }

      const payload: any = {
        name: value.name.trim(),
        status: value.status,
        ...(!isEdit && { type: value.type }),
        description: value.description,
        short_description: value.short_description,
        categories: value.categories.map((id) => ({ id })),
        images: buildImagesPayload(value.featuredImage, value.galleryImages),
      };

      if (value.type === "variable") {
        payload.attributes = value.attributes
          .filter((a) => a.name.trim() && parseOptions(a.options).length > 0)
          .map((a, i) => ({
            name: a.name.trim(),
            options: parseOptions(a.options),
            position: i,
            visible: true,
            variation: true,
          }));
      } else {
        payload.regular_price = value.regular_price || undefined;
        payload.sale_price = value.sale_price || undefined;
        payload.tax_status = value.tax_status;
        payload.tax_class = value.tax_class;
        payload.sku = value.sku || undefined;
        payload.manage_stock = value.manage_stock;
        payload.stock_quantity = value.manage_stock ? Number(value.stock_quantity) || 0 : null;
        payload.stock_status = value.stock_status;
      }

      try {
        let savedProduct: any;
        if (isEdit) {
          savedProduct = await updateMut.mutateAsync({ productId: product.id, ...payload });
          toast.success("Proizvod ažuriran");
        } else {
          savedProduct = await createMut.mutateAsync(payload);
          toast.success("Proizvod kreiran");
        }

        if (value.type === "variable" && value.variants.length > 0 && savedProduct?.id) {
          await saveVariations(savedProduct.id, value.variants);
        }

        onSuccess();
      } catch (err: any) {
        toast.error(err?.message || "Greška pri spremanju proizvoda");
      }
    },
  });

  function buildImagesPayload(featuredImage: any, galleryImages: any[]) {
    const images: any[] = [];
    if (featuredImage) images.push({ id: featuredImage.id, src: featuredImage.src });
    galleryImages.forEach((img) => images.push({ id: img.id, src: img.src }));
    return images;
  }

  async function saveVariations(productId: number, variantRows: VariationRow[]) {
    const existingIds = variantRows.filter((r) => r.id).map((r) => r.id!);
    const toCreate = variantRows.filter((r) => !r.id).map((r) => ({
      regular_price: r.regular_price,
      sale_price: r.sale_price || undefined,
      sku: r.sku || undefined,
      stock_quantity: r.stock_quantity ? Number(r.stock_quantity) : undefined,
      stock_status: r.stock_status,
      attributes: Object.entries(r.combination).map(([name, option]) => ({ name, option })),
      ...(r.image && { image: { id: r.image.id } }),
    }));
    const toUpdate = variantRows.filter((r) => r.id).map((r) => ({
      id: r.id!,
      regular_price: r.regular_price,
      sale_price: r.sale_price || undefined,
      sku: r.sku || undefined,
      stock_quantity: r.stock_quantity ? Number(r.stock_quantity) : undefined,
      stock_status: r.stock_status,
      ...(r.image ? { image: { id: r.image.id } } : {}),
    }));

    const oldVariationIds = (product?.variations || []).map((v: any) => v.id);
    const toDelete = oldVariationIds.filter((id: number) => !existingIds.includes(id));

    const batch: any = {};
    if (toCreate.length) batch.create = toCreate;
    if (toUpdate.length) batch.update = toUpdate;
    if (toDelete.length) batch.delete = toDelete;

    if (Object.keys(batch).length > 0) {
      await batchVarMut.mutateAsync({ productId, ...batch });
    }
  }

  async function handleImageUpload(file: File, isFeatured: boolean) {
    try {
      const result = await uploadMut.mutateAsync(file);
      if (isFeatured) {
        form.setFieldValue("featuredImage", { id: result.id, src: result.source_url });
      } else {
        const current = form.getFieldValue("galleryImages");
        form.setFieldValue("galleryImages", [...current, { id: result.id, src: result.source_url }]);
      }
      toast.success("Slika učitana");
    } catch (err: any) {
      toast.error(err?.message || "Greška pri uploadu slike");
    }
  }

  async function handleImageRemove(img: any, isFeatured: boolean) {
    try {
      if (img.id) await deleteImageMut.mutateAsync(img.id);
      if (isFeatured) {
        form.setFieldValue("featuredImage", null);
      } else {
        const current = form.getFieldValue("galleryImages");
        form.setFieldValue("galleryImages", current.filter((gi: any) => gi.id !== img.id));
      }
    } catch (err: any) {
      toast.error(err?.message || "Greška pri brisanju slike");
    }
  }

  async function handleVariantImageUpload(index: number, file: File) {
    try {
      const result = await uploadMut.mutateAsync(file);
      const variants = form.getFieldValue("variants");
      form.setFieldValue("variants", variants.map((r: VariationRow, i: number) =>
        i === index ? { ...r, image: { id: result.id, src: result.source_url } } : r
      ));
      toast.success("Slika varijante učitana");
    } catch (err: any) {
      toast.error(err?.message || "Greška pri uploadu slike");
    }
  }

  function removeVariantImage(index: number) {
    const variants = form.getFieldValue("variants");
    form.setFieldValue("variants", variants.map((r: VariationRow, i: number) =>
      i === index ? { ...r, image: null } : r
    ));
  }

  function generateVariants(attributes: Attribute[], currentVariants: VariationRow[]) {
    const combos = cartesian(attributes);
    const existingByKey = new Map(currentVariants.map((r) => [comboKey(r.combination), r]));
    const newRows: VariationRow[] = combos.map((combo) => {
      const existing = existingByKey.get(comboKey(combo));
      if (existing) return existing;
      return {
        combination: combo,
        regular_price: defaultPrice,
        sale_price: "",
        sku: "",
        stock_quantity: defaultStock,
        stock_status: "instock",
      };
    });
    form.setFieldValue("variants", newRows);
  }

  function applyDefaults(variants: VariationRow[]) {
    form.setFieldValue("variants", variants.map((r) => ({
      ...r,
      regular_price: r.regular_price || defaultPrice,
      stock_quantity: r.stock_quantity || defaultStock,
    })));
  }

  const isPending = createMut.isPending || updateMut.isPending || batchVarMut.isPending;

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        e.stopPropagation();
        form.handleSubmit();
      }}
      className="flex flex-col gap-3"
    >
      <section className="ring-1 ring-foreground/10 p-3 flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <h3 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Općenito</h3>
          <form.Field name="status">
            {(field) => (
              <Select value={field.state.value} onValueChange={(v: "publish" | "draft") => field.handleChange(v)}>
                <SelectTrigger className="h-7 text-xs w-auto gap-1.5">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="publish">Aktivan</SelectItem>
                  <SelectItem value="draft">Skica</SelectItem>
                </SelectContent>
              </Select>
            )}
          </form.Field>
        </div>
        <div className="flex flex-col gap-1">
          <Label className="text-xs">Naziv proizvoda *</Label>
          <form.Field name="name">
            {(field) => (
              <Input className="h-8 text-xs" value={field.state.value} onChange={(e) => field.handleChange(e.target.value)} placeholder="Naziv proizvoda" />
            )}
          </form.Field>
        </div>
        <form.Field name="type">
          {(typeField) => {
            const isVariable = typeField.state.value === "variable";
            return (
              <div className={`grid gap-3 ${isVariable ? "grid-cols-1" : "grid-cols-1 sm:grid-cols-2"}`}>
                <div className="flex flex-col gap-1">
                  <Label className="text-xs">Tip proizvoda</Label>
                  <Select value={typeField.state.value} onValueChange={(v) => typeField.handleChange(v)}>
                    <SelectTrigger className="h-8 text-xs w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="simple">Jednostavni</SelectItem>
                      <SelectItem value="variable">Varijabilni</SelectItem>
                      <SelectItem value="grouped">Grupni</SelectItem>
                      <SelectItem value="external">Vanjski</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {!isVariable && (
                  <div className="flex flex-col gap-1">
                    <Label className="text-xs">SKU</Label>
                    <form.Field name="sku">
                      {(field) => (
                        <Input className="h-8 text-xs font-mono" value={field.state.value} onChange={(e) => field.handleChange(e.target.value)} placeholder="SKU-001" />
                      )}
                    </form.Field>
                  </div>
                )}
              </div>
            );
          }}
        </form.Field>
        <div className="flex flex-col gap-1">
          <Label className="text-xs">Opis</Label>
          <form.Field name="description">
            {(field) => (
              <RichEditor value={field.state.value} onChange={(html) => field.handleChange(html)} />
            )}
          </form.Field>
        </div>
        <div className="flex flex-col gap-1">
          <Label className="text-xs">Kratki opis</Label>
          <form.Field name="short_description">
            {(field) => (
              <RichEditor value={field.state.value} onChange={(html) => field.handleChange(html)} />
            )}
          </form.Field>
        </div>
      </section>

      <form.Subscribe selector={(state) => state.values.type}>
        {(type) => type !== "variable" && (
          <section className="ring-1 ring-foreground/10 p-3 flex flex-col gap-3">
            <h3 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Cijene & Inventar</h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="flex flex-col gap-1">
                <Label className="text-xs">Redovna cijena *</Label>
                <form.Field name="regular_price">
                  {(field) => (
                    <Input className="h-8 text-xs" type="number" step="0.01" min="0" value={field.state.value} onChange={(e) => field.handleChange(e.target.value)} placeholder="0.00" />
                  )}
                </form.Field>
              </div>
              <div className="flex flex-col gap-1">
                <Label className="text-xs">Akcijska cijena</Label>
                <form.Field name="sale_price">
                  {(field) => (
                    <Input className="h-8 text-xs" type="number" step="0.01" min="0" value={field.state.value} onChange={(e) => field.handleChange(e.target.value)} placeholder="0.00" />
                  )}
                </form.Field>
              </div>
              <div className="flex flex-col gap-1">
                <Label className="text-xs">Porezni status</Label>
                <form.Field name="tax_status">
                  {(field) => (
                    <Select value={field.state.value} onValueChange={(v) => field.handleChange(v)}>
                      <SelectTrigger className="h-8 text-xs w-full">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="taxable">Oporezivo</SelectItem>
                        <SelectItem value="shipping">Dostava</SelectItem>
                        <SelectItem value="none">Neoporezivo</SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                </form.Field>
              </div>
              <div className="flex flex-col gap-1">
                <Label className="text-xs">Porezna klasa</Label>
                <form.Field name="tax_class">
                  {(field) => (
                    <Select value={field.state.value || "standard"} onValueChange={(v) => field.handleChange(v === "standard" ? "" : v)}>
                      <SelectTrigger className="h-8 text-xs w-full">
                        <SelectValue placeholder="Standardna" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="standard">Standardna</SelectItem>
                        <SelectItem value="reduced-rate">Reducirana</SelectItem>
                        <SelectItem value="zero-rate">Nulta</SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                </form.Field>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
              <form.Field name="manage_stock">
                {(field) => (
                  <label className="flex items-center gap-2 text-xs h-8">
                    <Checkbox checked={field.state.value} onCheckedChange={(v) => field.handleChange(!!v)} />
                    Upravljaj zalihama
                  </label>
                )}
              </form.Field>
              <form.Subscribe selector={(state) => state.values.manage_stock}>
                {(manageStock) => manageStock && (
                  <div className="flex flex-col gap-1">
                    <Label className="text-xs">Broj na stanju</Label>
                    <form.Field name="stock_quantity">
                      {(field) => (
                        <Input className="h-8 text-xs" type="number" min="0" value={field.state.value} onChange={(e) => field.handleChange(e.target.value)} placeholder="0" />
                      )}
                    </form.Field>
                  </div>
                )}
              </form.Subscribe>
              <div className="flex flex-col gap-1">
                <Label className="text-xs">Status zaliha</Label>
                <form.Field name="stock_status">
                  {(field) => (
                    <Select value={field.state.value} onValueChange={(v) => field.handleChange(v)}>
                      <SelectTrigger className="h-8 text-xs w-full">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="instock">Na stanju</SelectItem>
                        <SelectItem value="outofstock">Nedostupno</SelectItem>
                        <SelectItem value="onbackorder">Narudžba</SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                </form.Field>
              </div>
            </div>
          </section>
        )}
      </form.Subscribe>

      <form.Subscribe selector={(state) => state.values.type}>
        {(type) => type === "variable" && (
          <VariantsSection form={form} defaultPrice={defaultPrice} setDefaultPrice={setDefaultPrice} defaultStock={defaultStock} setDefaultStock={setDefaultStock} generateVariants={generateVariants} applyDefaults={applyDefaults} handleVariantImageUpload={handleVariantImageUpload} removeVariantImage={removeVariantImage} />
        )}
      </form.Subscribe>

      <section className="ring-1 ring-foreground/10 p-3 flex flex-col gap-2">
        <h3 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Kategorije</h3>
        <form.Field name="categories">
          {(field) => (
            <div className="flex flex-wrap gap-x-4 gap-y-1 max-h-[160px] overflow-y-auto">
              {categoryList?.map((cat: any) => (
                <label key={cat.id} className="flex items-center gap-2 text-xs py-0.5">
                  <Checkbox
                    checked={field.state.value.includes(cat.id)}
                    onCheckedChange={(v) => {
                      field.handleChange(v
                        ? [...field.state.value, cat.id]
                        : field.state.value.filter((id) => id !== cat.id)
                      );
                    }}
                  />
                  <span>{cat.name}</span>
                </label>
              ))}
              {!categoryList?.length && (
                <p className="text-muted-foreground text-xs">Nema kategorija</p>
              )}
            </div>
          )}
        </form.Field>
      </section>

      <section className="ring-1 ring-foreground/10 p-3 flex flex-col gap-3">
        <h3 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Slike</h3>
        <form.Subscribe selector={(state) => ({ featuredImage: state.values.featuredImage, galleryImages: state.values.galleryImages })}>
          {({ featuredImage, galleryImages }) => (
            <div className="flex gap-3 flex-wrap">
              {featuredImage ? (
                <div className="relative">
                  <img src={featuredImage.src} alt="" className="w-24 h-24 object-cover ring-1 ring-foreground/10" />
                  <Button
                    type="button"
                    variant="destructive"
                    size="icon-xs"
                    className="absolute top-0.5 right-0.5 h-4 w-4"
                    onClick={() => handleImageRemove(featuredImage, true)}
                  >
                    <X className="h-2.5 w-2.5" />
                  </Button>
                  <span className="absolute bottom-0 left-0 right-0 bg-background/80 text-[9px] text-center py-0.5">Glavna</span>
                </div>
              ) : (
                <div
                  className="w-24 h-24 ring-1 ring-foreground/10 ring-dashed flex flex-col items-center justify-center gap-0.5 cursor-pointer hover:bg-muted/50 transition-colors"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <Upload className="h-4 w-4 text-muted-foreground" />
                  <span className="text-[9px] text-muted-foreground">Glavna</span>
                </div>
              )}
              {galleryImages.map((img: any) => (
                <div key={img.id} className="relative">
                  <img src={img.src} alt="" className="w-24 h-24 object-cover ring-1 ring-foreground/10" />
                  <Button
                    type="button"
                    variant="destructive"
                    size="icon-xs"
                    className="absolute top-0.5 right-0.5 h-4 w-4"
                    onClick={() => handleImageRemove(img, false)}
                  >
                    <X className="h-2.5 w-2.5" />
                  </Button>
                </div>
              ))}
              <div
                className="w-24 h-24 ring-1 ring-foreground/10 ring-dashed flex flex-col items-center justify-center gap-0.5 cursor-pointer hover:bg-muted/50 transition-colors"
                onClick={() => galleryInputRef.current?.click()}
              >
                <Plus className="h-4 w-4 text-muted-foreground" />
                <span className="text-[9px] text-muted-foreground">Galerija</span>
              </div>
            </div>
          )}
        </form.Subscribe>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) handleImageUpload(file, true);
            e.target.value = "";
          }}
        />
        <input
          ref={galleryInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) handleImageUpload(file, false);
            e.target.value = "";
          }}
        />
      </section>

      <div className="flex justify-end pt-1">
        <Button type="submit" size="sm" disabled={isPending}>
          {isPending && <Loader2 className="h-3 w-3 mr-1 animate-spin" />}
          {isEdit ? "Spremi" : "Kreiraj"}
        </Button>
      </div>
    </form>
  );
}

function VariantsSection({
  form,
  defaultPrice,
  setDefaultPrice,
  defaultStock,
  setDefaultStock,
  generateVariants,
  applyDefaults,
  handleVariantImageUpload,
  removeVariantImage,
}: {
  form: any;
  defaultPrice: string;
  setDefaultPrice: (v: string) => void;
  defaultStock: string;
  setDefaultStock: (v: string) => void;
  generateVariants: (attrs: Attribute[], variants: VariationRow[]) => void;
  applyDefaults: (variants: VariationRow[]) => void;
  handleVariantImageUpload: (index: number, file: File) => void;
  removeVariantImage: (index: number) => void;
}) {
  return (
    <section className="ring-1 ring-foreground/10 p-3 flex flex-col gap-3">
      <h3 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Varijante</h3>

      <form.Field name="attributes">
        {(attrField: any) => {
          const attributes: Attribute[] = attrField.state.value;
          const generatedCombos = cartesian(attributes);

          return (
            <>
              <div className="flex flex-col gap-2">
                {attributes.map((attr: Attribute, i: number) => (
                  <div key={i} className="grid grid-cols-[1fr_2fr_auto] gap-2 items-center">
                    <Input
                      className="h-8 text-xs"
                      placeholder="Naziv (npr. Veličina)"
                      value={attr.name}
                      onChange={(e) => attrField.handleChange(attributes.map((a: Attribute, j: number) => j === i ? { ...a, name: e.target.value } : a))}
                    />
                    <Input
                      className="h-8 text-xs"
                      placeholder="Opcije odvojene zarezom (S, M, L, XL)"
                      value={attr.options}
                      onChange={(e) => attrField.handleChange(attributes.map((a: Attribute, j: number) => j === i ? { ...a, options: e.target.value } : a))}
                    />
                    <Button type="button" variant="ghost" size="icon-xs" onClick={() => attrField.handleChange(attributes.filter((_: any, j: number) => j !== i))}>
                      <X className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                ))}
                <div className="flex gap-2">
                  <Button type="button" variant="outline" size="sm" onClick={() => attrField.handleChange([...attributes, { name: "", options: "" }])}>
                    <Plus className="h-3.5 w-3.5 mr-1" />
                    Dodaj atribut
                  </Button>
                </div>
              </div>

              {generatedCombos.length > 0 && (
                <form.Subscribe selector={(state: any) => state.values.variants}>
                  {(variants: VariationRow[]) => (
                    <div className="grid grid-cols-[auto_auto_1fr] gap-2 items-end pt-2 border-t border-foreground/5">
                      <div className="flex flex-col gap-1">
                        <Label className="text-xs text-muted-foreground">Zadana cijena</Label>
                        <Input className="h-8 text-xs w-24" type="number" step="0.01" min="0" value={defaultPrice} onChange={(e) => setDefaultPrice(e.target.value)} placeholder="0.00" />
                      </div>
                      <div className="flex flex-col gap-1">
                        <Label className="text-xs text-muted-foreground">Zadano stanje</Label>
                        <Input className="h-8 text-xs w-20" type="number" min="0" value={defaultStock} onChange={(e) => setDefaultStock(e.target.value)} placeholder="0" />
                      </div>
                      <div className="flex items-center gap-2 h-8">
                        <Button type="button" variant="outline" size="sm" onClick={() => generateVariants(attributes, variants)}>
                          Generiraj ({generatedCombos.length})
                        </Button>
                        {variants.length > 0 && (
                          <Button type="button" variant="outline" size="sm" onClick={() => applyDefaults(variants)}>
                            Primijeni na prazne
                          </Button>
                        )}
                      </div>
                    </div>
                  )}
                </form.Subscribe>
              )}
            </>
          );
        }}
      </form.Field>

      <form.Field name="variants">
        {(varField: any) => {
          const variantRows: VariationRow[] = varField.state.value;
          if (variantRows.length === 0) return null;

          return (
            <>
            <VariantBulkActions variants={variantRows} onApply={(updated) => varField.handleChange(updated)} />
            <VariantTable
              variants={variantRows}
              onChange={(updated) => varField.handleChange(updated)}
              handleVariantImageUpload={handleVariantImageUpload}
              removeVariantImage={removeVariantImage}
            />
            </>
          );
        }}
      </form.Field>
    </section>
  );
}

function VariantTable({
  variants,
  onChange,
  handleVariantImageUpload,
  removeVariantImage,
}: {
  variants: VariationRow[];
  onChange: (v: VariationRow[]) => void;
  handleVariantImageUpload: (index: number, file: File) => void;
  removeVariantImage: (index: number) => void;
}) {
  function updateField(index: number, field: keyof VariationRow, value: string) {
    onChange(variants.map((r, j) => j === index ? { ...r, [field]: value } : r));
  }

  const [dragIndex, setDragIndex] = React.useState<number | null>(null);
  const [overIndex, setOverIndex] = React.useState<number | null>(null);

  function handleDragStart(e: React.DragEvent, index: number) {
    setDragIndex(index);
    e.dataTransfer.effectAllowed = "move";
  }

  function handleDragOver(e: React.DragEvent, index: number) {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setOverIndex(index);
  }

  function handleDrop(e: React.DragEvent, dropIndex: number) {
    e.preventDefault();
    if (dragIndex === null || dragIndex === dropIndex) {
      setDragIndex(null);
      setOverIndex(null);
      return;
    }
    const updated = [...variants];
    const [moved] = updated.splice(dragIndex, 1) as [VariationRow];
    updated.splice(dropIndex, 0, moved);
    onChange(updated);
    setDragIndex(null);
    setOverIndex(null);
  }

  function handleDragEnd() {
    setDragIndex(null);
    setOverIndex(null);
  }

  return (
    <div className="ring-1 ring-foreground/10 overflow-x-auto">
      <table className="w-full text-xs">
        <thead>
          <tr className="border-b bg-muted/50">
            <th className="w-6"></th>
            <th className="px-1.5 py-1 text-left font-medium w-10">Slika</th>
            <th className="px-1.5 py-1 text-left font-medium">Varijacija</th>
            <th className="px-1.5 py-1 text-left font-medium w-32">SKU</th>
            <th className="px-1.5 py-1 text-right font-medium w-28">Cijena</th>
            <th className="px-1.5 py-1 text-right font-medium w-28">Akcijska</th>
            <th className="px-1.5 py-1 text-right font-medium w-16">Stanje</th>
            <th className="px-1.5 py-1 text-left font-medium w-24">Status</th>
            <th className="px-1 py-1 w-6"></th>
          </tr>
        </thead>
        <tbody>
          {variants.map((row, i) => (
            <tr
              key={i}
              draggable
              onDragStart={(e) => handleDragStart(e, i)}
              onDragOver={(e) => handleDragOver(e, i)}
              onDrop={(e) => handleDrop(e, i)}
              onDragEnd={handleDragEnd}
              className={`border-b last:border-b-0 transition-colors ${
                dragIndex === i
                  ? "opacity-40"
                  : overIndex === i && dragIndex !== null
                    ? "bg-accent"
                    : "hover:bg-muted/50"
              }`}
            >
              <td className="px-0.5 py-0.5">
                <div className="cursor-grab active:cursor-grabbing flex items-center justify-center h-full">
                  <GripVertical className="h-3.5 w-3.5 text-muted-foreground" />
                </div>
              </td>
              <td className="px-1.5 py-0.5">
                {row.image ? (
                  <div className="relative w-8 h-8 group">
                    <img src={row.image.src} className="w-8 h-8 object-cover ring-1 ring-foreground/10" />
                    <button
                      type="button"
                      onClick={() => removeVariantImage(i)}
                      className="absolute -top-1 -right-1 bg-background ring-1 ring-foreground/10 p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <X className="h-2.5 w-2.5" />
                    </button>
                  </div>
                ) : (
                  <label className="w-8 h-8 flex items-center justify-center ring-1 ring-foreground/10 cursor-pointer hover:bg-muted/80 transition-colors">
                    <ImagePlus className="h-3.5 w-3.5 text-muted-foreground" />
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) handleVariantImageUpload(i, f);
                        e.target.value = "";
                      }}
                    />
                  </label>
                )}
              </td>
              <td className="px-1.5 py-0.5 whitespace-nowrap">
                {Object.values(row.combination).join(" / ")}
              </td>
              <td className="px-1.5 py-0.5">
                <Input
                  className="h-7 text-xs font-mono"
                  value={row.sku}
                  onChange={(e) => updateField(i, "sku", e.target.value)}
                />
              </td>
              <td className="px-1.5 py-0.5">
                <Input
                  className="h-7 text-xs"
                  type="number"
                  step="0.01"
                  value={row.regular_price}
                  onChange={(e) => updateField(i, "regular_price", e.target.value)}
                />
              </td>
              <td className="px-1.5 py-0.5">
                <Input
                  className="h-7 text-xs"
                  type="number"
                  step="0.01"
                  value={row.sale_price}
                  onChange={(e) => updateField(i, "sale_price", e.target.value)}
                />
              </td>
              <td className="px-1.5 py-0.5">
                <Input
                  className="h-7 text-xs"
                  type="number"
                  value={row.stock_quantity}
                  onChange={(e) => updateField(i, "stock_quantity", e.target.value)}
                />
              </td>
              <td className="px-1.5 py-0.5">
                <Select value={row.stock_status} onValueChange={(val) => updateField(i, "stock_status", val)}>
                  <SelectTrigger className="h-7 text-xs w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="instock">Na stanju</SelectItem>
                    <SelectItem value="outofstock">Nedostupno</SelectItem>
                    <SelectItem value="onbackorder">Narudžba</SelectItem>
                  </SelectContent>
                </Select>
              </td>
              <td className="px-0.5 py-0.5">
                <Button type="button" variant="ghost" size="icon-xs" onClick={() => {
                  onChange(variants.filter((_, j) => j !== i));
                }}>
                  <X className="h-3 w-3" />
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

type BulkAction =
  | "set_regular" | "increase_regular" | "decrease_regular"
  | "set_sale" | "increase_sale" | "decrease_sale";

const bulkActionLabels: Record<BulkAction, string> = {
  set_regular: "Postavi redovnu cijenu",
  increase_regular: "Povećaj redovnu cijenu",
  decrease_regular: "Smanji redovnu cijenu",
  set_sale: "Postavi akcijsku cijenu",
  increase_sale: "Povećaj akcijsku cijenu",
  decrease_sale: "Smanji akcijsku cijenu",
};

function applyBulkAction(variants: VariationRow[], action: BulkAction, rawValue: string): VariationRow[] {
  const isPercent = rawValue.trim().endsWith("%");
  const num = parseFloat(rawValue.replace("%", "").trim());
  if (isNaN(num) || num < 0) return variants;

  return variants.map((row) => {
    const copy = { ...row };
    const field = action.includes("regular") ? "regular_price" : "sale_price";
    const current = parseFloat(copy[field] || "0");

    if (action.startsWith("set_")) {
      copy[field] = num.toFixed(2);
    } else if (action.startsWith("increase_")) {
      const delta = isPercent ? current * (num / 100) : num;
      copy[field] = (current + delta).toFixed(2);
    } else if (action.startsWith("decrease_")) {
      const delta = isPercent ? current * (num / 100) : num;
      copy[field] = Math.max(0, current - delta).toFixed(2);
    }
    return copy;
  });
}

function VariantBulkActions({ variants, onApply }: { variants: VariationRow[]; onApply: (v: VariationRow[]) => void }) {
  const [activeAction, setActiveAction] = React.useState<BulkAction | null>(null);
  const [bulkValue, setBulkValue] = React.useState("");
  const inputRef = React.useRef<HTMLInputElement>(null);

  function handleSelect(action: BulkAction) {
    setActiveAction(action);
    setBulkValue("");
    setTimeout(() => inputRef.current?.focus(), 0);
  }

  function handleApply() {
    if (!activeAction || !bulkValue.trim()) return;
    onApply(applyBulkAction(variants, activeAction, bulkValue));
    setActiveAction(null);
    setBulkValue("");
  }

  return (
    <div className="flex items-center gap-2">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button type="button" variant="outline" size="sm">
            Uredi sve cijene
            <ChevronDown className="h-3.5 w-3.5 ml-1" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-64">
          <DropdownMenuLabel className="text-[10px] uppercase tracking-wide text-muted-foreground">Redovna cijena</DropdownMenuLabel>
          <DropdownMenuItem onClick={() => handleSelect("set_regular")}>Postavi redovnu cijenu</DropdownMenuItem>
          <DropdownMenuItem onClick={() => handleSelect("increase_regular")}>Povećaj redovnu cijenu</DropdownMenuItem>
          <DropdownMenuItem onClick={() => handleSelect("decrease_regular")}>Smanji redovnu cijenu</DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuLabel className="text-[10px] uppercase tracking-wide text-muted-foreground">Akcijska cijena</DropdownMenuLabel>
          <DropdownMenuItem onClick={() => handleSelect("set_sale")}>Postavi akcijsku cijenu</DropdownMenuItem>
          <DropdownMenuItem onClick={() => handleSelect("increase_sale")}>Povećaj akcijsku cijenu</DropdownMenuItem>
          <DropdownMenuItem onClick={() => handleSelect("decrease_sale")}>Smanji akcijsku cijenu</DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {activeAction && (
        <div className="flex items-center gap-1.5">
          <span className="text-xs text-muted-foreground">{bulkActionLabels[activeAction]}:</span>
          <Input
            ref={inputRef}
            className="h-7 text-xs w-28"
            placeholder="10 ili 10%"
            value={bulkValue}
            onChange={(e) => setBulkValue(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); handleApply(); } }}
          />
          <Button type="button" variant="outline" size="icon-xs" onClick={handleApply}>
            <Check className="h-3.5 w-3.5" />
          </Button>
          <Button type="button" variant="ghost" size="icon-xs" onClick={() => { setActiveAction(null); setBulkValue(""); }}>
            <X className="h-3.5 w-3.5" />
          </Button>
        </div>
      )}
    </div>
  );
}
